import { useCallback, useEffect, useRef, useState } from 'react';
import { integrationService } from '@/services';
import { readMetaAuthTypes, type MetaAuthType } from '../meta-contract';

export interface UseMetaAuthTypesResult {
	/** null while unknown — rendering anything before the answer flashes the wrong state. */
	authTypes: MetaAuthType[] | null;
	/** The last read failed; the card offers a retry instead of declaring the environment off. */
	failed: boolean;
	reload: () => Promise<void>;
}

/**
 * Which Meta connect routes this deployment can offer.
 *
 * A deployment fact, not an account one, so it comes from machine and not from the integrations
 * list — which carries no `meta` row at all until something connects. A failed read is kept apart
 * from "none enabled": the first is transient and retryable, the second is a support question,
 * and showing the second for the first sends the customer to support for a blip.
 */
export const useMetaAuthTypes = (): UseMetaAuthTypesResult => {
	const [authTypes, setAuthTypes] = useState<MetaAuthType[] | null>(null);
	const [failed, setFailed] = useState(false);
	const mountedRef = useRef(true);

	const reload = useCallback(async () => {
		setFailed(false);
		try {
			const config = await integrationService.getMetaChannelConfig();
			if (!mountedRef.current) return;
			setAuthTypes(readMetaAuthTypes(config.availableAuthTypes));
		} catch (err) {
			console.error('[useMetaAuthTypes.reload] getMetaChannelConfig failed', err);
			if (!mountedRef.current) return;
			setFailed(true);
		}
	}, []);

	useEffect(() => {
		mountedRef.current = true;
		void reload();
		return () => {
			mountedRef.current = false;
		};
	}, [reload]);

	return { authTypes, failed, reload };
};
